import { useState, useMemo } from "react";
import { useAiTools, useUpdateAiTool, useBulkUpdateAiTools } from "@/hooks/useAiTools";
import type { AiToolRecord } from "@/hooks/useAiTools";
import { Button, PageHeader, Toggle, Badge } from "@/components/ui";
import { Icon } from "@/components/IconSprite";
import { clsx } from "clsx";

const SettingsTools = () => {
  const { data: tools, isLoading } = useAiTools();
  const updateMutation = useUpdateAiTool();
  const bulkUpdateMutation = useBulkUpdateAiTools();
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const groupedTools = useMemo(() => {
    if (!tools) return {};
    const groups: Record<string, AiToolRecord[]> = {};
    for (const tool of tools) {
      const category = tool.category || "uncategorized";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(tool);
    }
    return groups;
  }, [tools]);

  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return groupedTools;
    const term = searchTerm.toLowerCase();
    const filtered: Record<string, AiToolRecord[]> = {};
    for (const [category, categoryTools] of Object.entries(groupedTools)) {
      const matchingTools = categoryTools.filter(
        (tool) =>
          tool.tool_id.toLowerCase().includes(term) ||
          tool.tool_name.toLowerCase().includes(term) ||
          (tool.description && tool.description.toLowerCase().includes(term))
      );
      if (matchingTools.length > 0) {
        filtered[category] = matchingTools;
      }
    }
    return filtered;
  }, [groupedTools, searchTerm]);

  const categories = Object.keys(filteredGroups).sort();

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const expandAll = () => setExpandedCategories(new Set(categories));
  const collapseAll = () => setExpandedCategories(new Set());

  const handleToggleTool = async (toolId: string, currentEnabled: boolean) => {
    try {
      await updateMutation.mutateAsync({ tool_id: toolId, is_enabled: !currentEnabled });
    } catch (err) {
      console.error("Failed to update tool:", err);
    }
  };

  const handleToggleCategory = async (category: string, enable: boolean) => {
    const categoryTools = filteredGroups[category] || [];
    const updates = categoryTools.map((tool) => ({
      tool_id: tool.tool_id,
      is_enabled: enable,
    }));
    try {
      await bulkUpdateMutation.mutateAsync(updates);
    } catch (err) {
      console.error("Failed to bulk update tools:", err);
    }
  };

  const getCategoryStats = (category: string) => {
    const categoryTools = filteredGroups[category] || [];
    const enabled = categoryTools.filter((t) => t.is_enabled).length;
    return { enabled, total: categoryTools.length };
  };

  const formatCategoryName = (category: string) => {
    return category.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <span className="text-sm text-slate-500">Loading tools...</span>
      </div>
    );
  }

  const totalTools = tools?.length || 0;
  const enabledCount = tools?.filter((t) => t.is_enabled).length || 0;
  const disabledCount = totalTools - enabledCount;
  const totalCategories = Object.keys(groupedTools).length;

  return (
    <div className="page" style={{ maxWidth: 1000 }}>
      <PageHeader
        label="Settings"
        title="Tools Management"
        description="Enable or disable individual AI tools. Disabled tools will not be available to the assistant."
      />

      {/* Stats */}
      <div className="stats" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
        <div className="stat">
          <div className="stat-h">
            <span className="stat-i">
              <Icon name="wrench" />
            </span>
            <span className="lb">Total Tools</span>
          </div>
          <div className="stat-v">{totalTools}</div>
          <div className="stat-b">
            <span className="sl">Across {totalCategories} categories</span>
          </div>
        </div>
        <div className="stat">
          <div className="stat-h">
            <span className="stat-i g">
              <Icon name="check" />
            </span>
            <span className="lb">Enabled</span>
          </div>
          <div className="stat-v" style={{ color: "var(--ok)" }}>
            {enabledCount}
          </div>
          <div className="stat-b">
            <span className="sl">Available to the assistant</span>
          </div>
        </div>
        <div className="stat">
          <div className="stat-h">
            <span
              className="stat-i"
              style={{
                background: "var(--err-bg)",
                borderColor: "var(--err-bd)",
                color: "var(--err)",
              }}
            >
              <Icon name="x" />
            </span>
            <span className="lb">Disabled</span>
          </div>
          <div className="stat-v" style={{ color: "var(--err)" }}>
            {disabledCount}
          </div>
          <div className="stat-b">
            <span className="sl">Hidden from the model</span>
          </div>
        </div>
      </div>

      {/* Search and Controls */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="tbar" style={{ borderBottom: "none" }}>
          <div className="srch" style={{ maxWidth: 340 }}>
            <Icon name="search" />
            <input
              className="inp"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search tools..."
            />
          </div>
          <div className="r">
            <Button variant="secondary" size="sm" onClick={expandAll}>
              Expand All
            </Button>
            <Button variant="secondary" size="sm" onClick={collapseAll}>
              Collapse All
            </Button>
          </div>
        </div>
      </div>

      {/* Tool Categories */}
      {categories.length === 0 ? (
        <div className="empty sm">
          <div className="empty-i">
            <Icon name="wrench" />
          </div>
          <b>{searchTerm ? "No matching tools" : "No tools found"}</b>
          <p>{searchTerm ? "No tools match your search." : "No tools found."}</p>
        </div>
      ) : (
        categories.map((category) => {
          const isExpanded = expandedCategories.has(category);
          const stats = getCategoryStats(category);
          const categoryTools = filteredGroups[category] || [];

          return (
            <div key={category} className={clsx("acc", isExpanded && "open")}>
              <button type="button" className="acc-h" onClick={() => toggleCategory(category)}>
                <Icon name="r" className="ch" />
                <b>{formatCategoryName(category)}</b>
                <span className="cnt2">
                  {stats.enabled} of {stats.total} enabled
                </span>
                <span className="r">
                  <button
                    type="button"
                    className="act-txt"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleCategory(category, true);
                    }}
                    disabled={bulkUpdateMutation.isPending}
                  >
                    Enable All
                  </button>
                  <button
                    type="button"
                    className="act-txt"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleCategory(category, false);
                    }}
                    disabled={bulkUpdateMutation.isPending}
                  >
                    Disable All
                  </button>
                </span>
              </button>
              <div className="acc-b">
                {categoryTools.map((tool) => (
                  <div key={tool.tool_id} className="tool-r">
                    <div className="tx">
                      <div className="id">
                        {tool.tool_id}
                        {!tool.is_enabled && <Badge variant="error">Disabled</Badge>}
                      </div>
                      {tool.description && <p>{tool.description}</p>}
                    </div>
                    <Toggle
                      checked={tool.is_enabled}
                      onChange={() => handleToggleTool(tool.tool_id, tool.is_enabled)}
                      disabled={updateMutation.isPending}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default SettingsTools;
